import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
/**
 * How long to wait for further +/- taps before sending a quantity change. Taps within this window
 * are sent as one request - every cart mutation takes Shopify well over a second on this store, so
 * a request per tap would stack those up.
 */
const QUANTITY_BATCH_DELAY = 250;

/**
 * @typedef {object} QueuedQuantity
 * @property {string | undefined} key - The line item key (stable while other lines change or are removed).
 * @property {number} line - The 1-based line number, used when a key isn't available and for error messages.
 * @property {number} quantity - The requested quantity.
 */

class CartItemsComponent extends Component {
  /**
   * Quantity changes that haven't been sent yet, by line item key. A later change to the same line
   * replaces the earlier one, so only the latest requested quantity is ever sent.
   * @type {Map<string, QueuedQuantity>}
   */
  #queuedQuantities = new Map();

  /** @type {Promise<void> | null} The cart request currently in flight, if any. */
  #activeRequest = null;

  #scheduleQuantityFlush = debounce(() => this.#flushQuantities(), QUANTITY_BATCH_DELAY);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);

    // Page loaded with items already in the cart: fill "You May Also Like"
    // right away (the update call sites below only cover later cart changes;
    // the old Nosto placement was filled on page load by Nosto's own script,
    // which no longer owns this block).
    this.#loadCartRecommendations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    this.#scheduleQuantityFlush.cancel();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // The row the tapped selector lives in, not `cartItemRows[line - 1]`: a row removed a moment
    // ago is already gone from the DOM, which shifts that index, while the selector's line number
    // is only refreshed by the next render.
    const lineItemRow =
      (event.target instanceof Element && event.target.closest('.cart-items__table-row')) ||
      this.refs.cartItemRows[line - 1];

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
      row: lineItemRow instanceof HTMLElement ? lineItemRow : undefined,
      batch: true,
    });
  };

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    const rowsToRemove = cartItemRowToRemove
      ? [
          cartItemRowToRemove,
          // Get all nested lines of the row to remove
          ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
        ]
      : [];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length > 0 && rowsToRemove.length == this.refs.cartItemRows.length;

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
      row: cartItemRowToRemove,
      // Removing the last item swaps straight to the empty-cart state below -
      // no other cart content is left to show a loading state on.
      showLoadingState: !isEmptyCart,
    });

    if (!cartItemRowToRemove) return;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      if (this.isDrawer) {
        const dialog = this.closest('cart-drawer-component')?.querySelector('dialog.cart-drawer__dialog');
        dialog?.classList.add('cart-drawer--empty');
        dialog?.setAttribute('aria-labelledby', 'cart-drawer-heading-empty');
      }

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const { key } = row.dataset;
      // The cart response can be morphed in before the animation ends, and the morph matches rows by
      // position - this element may by then show the next line item. Only remove it if it's still
      // the line that was removed.
      const remove = () => {
        if (row.dataset.key === key) row.remove();
      };

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity of a line item.
   *
   * Requests are serialised: while one is in flight, further changes are queued and sent together
   * once it completes (the latest quantity per line wins), so responses can never arrive out of
   * order and overwrite a newer change. The cart stays in its loading state - checkout blocked,
   * updated rows and totals marked - until the last request has been applied.
   *
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   * @param {HTMLElement} [config.row] - The line item row being changed.
   * @param {boolean} [config.batch] - Wait briefly for further taps before sending (quantity buttons).
   * @param {boolean} [config.showLoadingState] - Whether to show the loading state while this update is
   *   in flight. Defaults to true; pass false when the UI is already moving straight to a different
   *   state (e.g. the empty-cart view) that shouldn't have a loading state flash over it.
   */
  updateQuantity(config) {
    const { line, quantity, action } = config;
    const row = config.row ?? this.refs.cartItemRows[line - 1];
    const key = row?.dataset.key || undefined;

    this.#queuedQuantities.set(key ?? `line:${line}`, { key, line, quantity });

    // No loading state on the line item's own price: the drawer shows the unit price, which a
    // quantity change doesn't alter. The subtotal is what changes.
    if (config.showLoadingState !== false) {
      this.#beginLoading();
      this.refs.cartTotal?.shimmer();
    }

    if (this.#activeRequest) return; // Sent as soon as the current request completes.

    if (config.batch) {
      this.#scheduleQuantityFlush();
    } else {
      this.#scheduleQuantityFlush.cancel();
      this.#flushQuantities(action);
    }
  }

  /**
   * Sends every queued quantity change in a single request, then keeps going until the queue is empty.
   * @param {string} [action] - Performance marker label.
   */
  #flushQuantities(action = 'change') {
    if (this.#activeRequest || this.#queuedQuantities.size === 0) return;

    const changes = Array.from(this.#queuedQuantities.values());
    this.#queuedQuantities.clear();

    const request = this.#sendQuantities(changes, action)
      .catch((error) => console.error(error))
      .finally(() => {
        this.#activeRequest = null;

        if (this.#queuedQuantities.size > 0) {
          this.#flushQuantities();
        } else {
          this.#endLoading();
        }
      });

    this.#activeRequest = request;
  }

  /**
   * @param {QueuedQuantity[]} changes
   * @param {string} action
   */
  async #sendQuantities(changes, action) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${action}:user-action`);

    const sectionsToUpdate = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const sections = {
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };

    // One line: /cart/change by key. Several lines changed together: one /cart/update for all of them.
    const [firstChange] = changes;
    const useUpdate = changes.length > 1 && changes.every((change) => change.key);
    const url = useUpdate ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;
    const payload = useUpdate
      ? { updates: Object.fromEntries(changes.map((change) => [change.key, change.quantity])), ...sections }
      : {
          ...(firstChange?.key ? { id: firstChange.key } : { line: firstChange?.line }),
          quantity: firstChange?.quantity,
          ...sections,
        };

    // Lines without a key can't be combined safely; send them one at a time.
    if (!useUpdate && changes.length > 1) {
      for (const change of changes.slice(1)) {
        this.#queuedQuantities.set(change.key ?? `line:${change.line}`, change);
      }
    }

    try {
      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
      const parsedResponseText = JSON.parse(await response.text());

      if (parsedResponseText.errors || (!response.ok && parsedResponseText.status)) {
        this.#handleCartError(firstChange?.line ?? 1, {
          errors: parsedResponseText.errors || parsedResponseText.description || parsedResponseText.message,
        });
        return;
      }

      // The shopper changed the cart again while this was in flight: that follow-up request renders the
      // final state, so applying this intermediate one would only flash old values.
      if (this.#queuedQuantities.size > 0) return;

      const newSectionHTML = new DOMParser().parseFromString(parsedResponseText.sections[this.sectionId], 'text/html');

      // Grab the new cart item count from a hidden element
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      // Update data-cart-quantity for all matching variants
      this.#updateQuantitySelectors(parsedResponseText);

      this.dispatchEvent(
        new CartUpdateEvent(parsedResponseText, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );

      morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
        mode: this.isDrawer ? 'hydration' : 'full',
      });

      // This component ignores its own `CartUpdateEvent`, so the morph above is the only one that
      // runs for a quantity change made inside the drawer.
      this.#loadCartRecommendations();
      this.#refreshWrappedApp();
      this.#updateShippingMessage(newSectionHTML);
      this.#updateCartQuantitySelectorButtonStates();
    } finally {
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    const morphMode = this.isDrawer ? 'hydration' : 'full';

    if (event instanceof DiscountUpdateEvent) {
      this.#beginLoading();
      sectionRenderer
        .renderSection(this.sectionId, { cache: false, mode: morphMode })
        .then(() => {
          this.#loadCartRecommendations();
          this.#refreshWrappedApp();
        })
        .finally(() => this.#endLoadingIfIdle());
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];

    // assets/cart-state-sync.js only sends the sections it found out of date - this one is current.
    if (event.detail.data.source === 'cart-state-sync' && !cartItemsHtml) return;
    if (!cartItemsHtml) {
      this.#beginLoading();
      sectionRenderer
        .renderSection(this.sectionId, { cache: false, mode: morphMode })
        .then(() => {
          this.#loadCartRecommendations();
          this.#refreshWrappedApp();
        })
        .finally(() => this.#endLoadingIfIdle());
      return;
    }

    const newSectionHTML = new DOMParser().parseFromString(cartItemsHtml, 'text/html');

    // A hydration morph only refreshes `data-hydration-key` targets that already exist in the DOM,
    // and the drawer's empty state renders none of them. Adding the first item therefore has to
    // swap the whole subtree, otherwise the drawer opens still saying the cart is empty.
    if (!this.#replaceEmptyState(newSectionHTML)) {
      // Items added elsewhere (add to cart, quick add).
      this.#beginLoading();

      morphSection(this.sectionId, cartItemsHtml, { mode: morphMode });

      // The shipping message sits outside every hydration target, so it never gets morphed.
      if (this.isDrawer) this.#updateShippingMessage(newSectionHTML);

      // Covers the case where the drawer already held items but Nosto hadn't filled the placement
      // yet — the empty-state path requests it on its own.
      this.#loadCartRecommendations();
      this.#refreshWrappedApp();
      this.#endLoadingIfIdle();
    }

    if (this.isDrawer) this.#syncDrawerEmptyState(newSectionHTML);

    // Update button states for all cart quantity selectors after morph
    this.#updateCartQuantitySelectorButtonStates();
  };

  /**
   * Replaces the drawer's empty-cart markup with the freshly rendered cart.
   *
   * Only applies to the drawer, and only while it is showing its empty state — the hydration morph
   * used everywhere else cannot introduce `data-hydration-key` targets that aren't already present.
   *
   * @param {Document} newSectionDocument - The newly rendered section
   * @returns {boolean} Whether the contents were replaced
   */
  #replaceEmptyState(newSectionDocument) {
    if (!this.isDrawer) return false;

    // Already showing hydratable cart markup — a normal morph handles it.
    if (this.querySelector('[data-hydration-key]')) return false;

    const newCartItems = newSectionDocument.querySelector('cart-items-component[data-drawer]');
    if (!newCartItems?.querySelector('[data-hydration-key]')) return false;

    // Adopted (not imported) so we don't construct a stray `cart-items-component`; the children are
    // upgraded when `replaceChildren` connects them to this document.
    const children = Array.from(newCartItems.childNodes);

    startViewTransition(() => {
      this.replaceChildren(...children);

      // The recommendation placement only exists in the markup while the cart has items, so it
      // lands in the DOM here — long after Nosto's page-load scan. Requested inside the callback
      // so the network request starts as soon as the nodes are connected, rather than waiting for
      // the transition to finish.
      this.#loadCartRecommendations();
      this.#refreshWrappedApp();
    }, ['empty-cart-drawer']);

    return true;
  }

  /**
   * Fills the drawer's "You May Also Like" block with recommendations for the
   * last product added to the cart (Nosto category-proxy first when
   * collection_provider is 'nosto', Shopify native related-products
   * otherwise/as fallback) — see NostoCartRecommendations' own doc comment.
   * Replaces the earlier `#cartpage-nosto-1` Nosto-placement approach
   * (`nostojs(api => api.loadRecommendations())`), whose failure mode was a
   * silent empty gap under the heading.
   *
   * With an empty cart the drawer renders no recommendation block at all, so
   * this is a no-op there; each cart update re-invokes it, and the helper
   * itself skips the work when the last-added product hasn't changed.
   */
  #loadCartRecommendations() {
    if (this.isDrawer) {
      const container = this.querySelector('[data-cart-drawer-recommendation]');
      if (!(container instanceof HTMLElement)) return;

      // Nothing to do here — the drawer's own `data-nosto-pending`
      // attribute (re-set fresh on every cart-update morph) is watched by
      // Nosto Search Templates' own MutationObserver
      // (src/entries/cartRecommendations.tsx's `initCartRecommendations`),
      // which fills this container itself, independent of this method.
      return;
    }

    // Cart PAGE "You may also like" (sections/product-list.liquid's
    // cart-context mode) lives in its own separate section — this
    // component's own morph (`morphSection(this.sectionId, ...)` above)
    // never touches it, so without this it stayed stuck on whatever the
    // last-added product was at initial page load: changing quantity or
    // removing a line item via AJAX (no full reload) never refreshed it.
    // Its `data-cart-last-product-id`/`data-cart-product-ids`/etc are
    // server-rendered from `cart.*` Liquid objects, so re-running
    // `maybeEnhance` on the stale DOM alone would just re-read the same
    // stale values — a real section re-fetch + morph is needed first to
    // get those attributes current, same as `sectionRenderer.getSectionHTML`
    // + `morphSection` are used elsewhere in this file.
    const container = document.querySelector('[data-testid="product-list"][data-cart-context="true"]');
    if (!(container instanceof HTMLElement)) return;

    const sectionId = container.getAttribute('section-id');
    if (!sectionId) return;

    Promise.all([import('@theme/section-renderer'), import('@theme/nosto-product-list-results')])
      .then(async ([{ sectionRenderer, morphSection }, { NostoProductListResultsHelper }]) => {
        const html = await sectionRenderer.getSectionHTML(sectionId, false);
        if (!html) return;
        morphSection(sectionId, html, { mode: 'full' });

        const refreshedContainer = document.querySelector('[data-testid="product-list"][data-cart-context="true"]');
        if (refreshedContainer instanceof HTMLElement) {
          await NostoProductListResultsHelper.maybeEnhance(refreshedContainer);
        }
      })
      .catch((error) => console.error('Cart page recommendations failed to load', error));
  }

  /**
   * Lets the Wrapped gift wrap app embed know the cart changed.
   *
   * Only Wrapped's own event: it doesn't listen for the generic `cart:updated` / `cart:refresh`
   * events (it only dispatches those, for other themes); other app embeds listening for those would
   * fetch the cart and re-render the drawer again after every change.
   */
  #refreshWrappedApp = debounce(() => {
    try {
      document.dispatchEvent(new CustomEvent('wrapped:cart:update', { bubbles: true }));
      const wrapped = /** @type {any} */ (window).Wrapped;
      if (typeof wrapped?.refresh === 'function') wrapped.refresh();
      if (typeof wrapped?.render === 'function') wrapped.render();
    } catch (error) {
      console.warn('[Wrapped] Cart refresh failed.', error);
    }
  }, 50);

  /**
   * Syncs the drawer dialog's empty-state class and heading label. The dialog is a parent of this
   * component, so neither the morph nor the empty-state swap above reaches it.
   *
   * @param {Document} newSectionDocument - The newly rendered section
   */
  #syncDrawerEmptyState(newSectionDocument) {
    const dialog = this.closest('cart-drawer-component')?.querySelector('dialog.cart-drawer__dialog');
    if (!dialog) return;

    // The standalone cart-drawer-contents section has no dialog, so it carries the same state on a marker.
    const newDialog = newSectionDocument.querySelector('dialog.cart-drawer__dialog');
    const state = /** @type {HTMLElement | null} */ (newSectionDocument.querySelector('[data-cart-drawer-state]'));
    if (!newDialog && !state) return;

    const isEmpty = newDialog ? newDialog.classList.contains('cart-drawer--empty') : state?.dataset.empty === 'true';
    dialog.classList.toggle('cart-drawer--empty', isEmpty);

    const labelledBy = newDialog ? newDialog.getAttribute('aria-labelledby') : state?.dataset.labelledby;
    if (labelledBy) dialog.setAttribute('aria-labelledby', labelledBy);
  }

  /**
   * Puts the cart into its loading state while an update is in flight.
   *
   * - `data-cart-updating` on the component drives the loading styles in
   *   cart-items-component.liquid: the totals show their loading state and checkout is blocked so the
   *   shopper can't check out a cart that's about to change. Quantity buttons stay usable: further
   *   taps are queued (see `updateQuantity`).
   */
  #beginLoading() {
    this.setAttribute('aria-busy', 'true');
    this.dataset.cartUpdating = 'true';

    for (const checkout of this.querySelectorAll('.cart__checkout-button')) {
      checkout.setAttribute('aria-disabled', 'true');
    }
  }

  /**
   * Clears the loading state once the last update has been fully applied.
   */
  #endLoading() {
    resetShimmer(this);
    this.removeAttribute('aria-busy');
    delete this.dataset.cartUpdating;

    for (const element of this.querySelectorAll('[data-cart-updating]')) {
      if (element instanceof HTMLElement) delete element.dataset.cartUpdating;
    }

    for (const checkout of this.querySelectorAll('.cart__checkout-button[aria-disabled]')) {
      checkout.removeAttribute('aria-disabled');
    }
  }


  /**
   * Ends a loading state started outside `updateQuantity` (cart updates from elsewhere), unless a
   * quantity update of this component is still running - that one ends it when it completes.
   */
  #endLoadingIfIdle() {
    if (this.#activeRequest || this.#queuedQuantities.size > 0) return;
    this.#endLoading();
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Replaces the current shipping message with the newly rendered Liquid HTML.
   *
   * @param {Document} newSectionDocument
   */
  #updateShippingMessage(newSectionDocument) {
    const messages = this.closest('cart-drawer-component')?.querySelector('.cart-drawer__messages');
    if (!messages) return;

    // The free-shipping message and the multibuy offer message (snippets/multibuy-offer.liquid)
    // are both plain Liquid, so each is swapped for its freshly rendered copy.
    for (const selector of ['.cart-drawer__shipping-message', '.cart-drawer__offer-message']) {
      const newMessage = newSectionDocument.querySelector(`.cart-drawer__messages ${selector}`);
      const currentMessage = messages.querySelector(selector);

      if (newMessage && currentMessage) {
        currentMessage.replaceWith(newMessage);
      } else if (newMessage) {
        if (selector === '.cart-drawer__shipping-message') messages.prepend(newMessage);
        else messages.append(newMessage);
      } else {
        currentMessage?.remove();
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId; 
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
  
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
